import { DeviceAuthorizationSettings } from '@mews-commander/data-access';
import { UnsavedChangesDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Button, Stack } from '@optimus-web/core';
import { ConfigureDeviceAuthPanelContent } from './ConfigureDeviceAuthPanelContent';
import { useConfigureDeviceAuthForm } from './useConfigureDeviceAuthForm';

export interface ConfigureDeviceAuthPanelProps {
    data?: DeviceAuthorizationSettings;
    active: boolean;
    onClose: () => void;
}

const formId = 'configure-device-auth-panel-form';

export function ConfigureDeviceAuthPanel({ data, onClose, ...props }: ConfigureDeviceAuthPanelProps) {
    const [{ active: showUnsavedChangesDialog }, toggleUnsavedChangesDialogVisibility] = useDialog();

    const { reset, isSubmitting, hasDirtyFields, submit, renderFormContent, isValid } = useConfigureDeviceAuthForm({
        id: formId,
        data,
        onSuccess: onClose,
    });

    const handleClose = hasDirtyFields ? toggleUnsavedChangesDialogVisibility : onClose;
    const handleSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        void submit();
    };

    return (
        <>
            <FloatingPanel
                onClose={handleClose}
                title={
                    <Stack spacing="150" itemAlignment="center">
                        <LocalizedText textKey={TextKey.DeviceAuthConfiguration} />
                    </Stack>
                }
                size="small"
                footer={{
                    primary: (
                        <Button
                            size="medium"
                            type="submit"
                            variant="primary"
                            form={formId}
                            loading={isSubmitting}
                            onClick={handleSubmit}
                            disabled={!hasDirtyFields || !isValid}
                        >
                            <LocalizedText textKey={TextKey.Save} />
                        </Button>
                    ),
                    secondary: (
                        <Button size="medium" variant="tertiary" type="button" onClick={handleClose}>
                            <LocalizedText textKey={TextKey.GoBack} />
                        </Button>
                    ),
                }}
                loading={isSubmitting}
                {...props}
            >
                <Stack vertical spacing="200" distribution="space-between" fill>
                    {renderFormContent(<ConfigureDeviceAuthPanelContent />)}
                    <Banner
                        variant="info"
                        secondaryText={<LocalizedText textKey={TextKey.DeviceAuthConfigurationBanner} />}
                    />
                </Stack>
            </FloatingPanel>
            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                onCancel={toggleUnsavedChangesDialogVisibility}
                onDiscardChanges={() => {
                    toggleUnsavedChangesDialogVisibility();
                    onClose();
                    reset();
                }}
            />
        </>
    );
}
